import React, { useState } from 'react';
import { Building, Users, DollarSign, MapPin, Eye, Edit, Plus, Filter, Search, Phone, Mail, FileText, CheckCircle, Clock, AlertTriangle, TrendingUp, PieChart, BarChart3, Calendar, CreditCard, Banknote, MessageSquare, Star, Shield } from 'lucide-react';

const AgenciesManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [filterDestination, setFilterDestination] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAgencyPortfolio, setShowAgencyPortfolio] = useState(false);
  const [showAgencyFinances, setShowAgencyFinances] = useState(false);

  // Données des agences
  const agencies = [
    {
      id: 1,
      name: 'Luxury Villas International',
      type: 'Premium Partner',
      location: 'Saint-Barthélemy',
      contact: {
        manager: 'Sophie Martineau',
        email: 'sophie@luxuryvillas.com',
        phone: '+590 690 12 34 56'
      },
      portfolio: {
        totalVillas: 47,
        activeVillas: 43,
        destinations: ['Saint-Barthélemy', 'Saint-Martin'],
        averagePrice: 25000,
        occupancyRate: 78.5
      },
      commission: {
        standard: 15,
        premium: 18,
        exclusive: 20
      },
      financials: {
        totalRevenue: 1250000,
        pendingPayments: 45000,
        lastPayment: '2024-09-10',
        rib: 'FR76 3000 4000 1100 0001 2345 67'
      },
      reservations: {
        total: 156,
        confirmed: 142,
        pending: 8,
        cancelled: 6
      },
      status: 'active',
      rating: 4.8,
      joinDate: '2019-03-15'
    },
    {
      id: 2,
      name: 'Mediterranean Elite Villas',
      type: 'Gold Partner',
      location: 'Mykonos',
      contact: {
        manager: 'Andreas Stavros',
        email: 'andreas@medelite.gr',
        phone: '+30 694 123 4567'
      },
      portfolio: {
        totalVillas: 32,
        activeVillas: 28,
        destinations: ['Mykonos', 'Santorini', 'Paros'],
        averagePrice: 18000,
        occupancyRate: 72.3
      },
      commission: {
        standard: 12,
        premium: 15,
        exclusive: 17
      },
      financials: {
        totalRevenue: 890000,
        pendingPayments: 23000,
        lastPayment: '2024-09-08',
        rib: 'GR16 0110 1250 0000 0001 2345 67'
      },
      reservations: {
        total: 98,
        confirmed: 89,
        pending: 5,
        cancelled: 4
      },
      status: 'active',
      rating: 4.6,
      joinDate: '2020-06-22'
    },
    {
      id: 3,
      name: 'Riviera Prestige Collection',
      type: 'Premium Partner',
      location: 'Cannes',
      contact: {
        manager: 'Marie-Claire Dubois',
        email: 'mc.dubois@riviera-prestige.fr',
        phone: '+33 6 12 34 56 78'
      },
      portfolio: {
        totalVillas: 61,
        activeVillas: 58,
        destinations: ['Cannes', 'Saint-Tropez', 'Nice', 'Monaco'],
        averagePrice: 32000,
        occupancyRate: 85.2
      },
      commission: {
        standard: 16,
        premium: 19,
        exclusive: 22
      },
      financials: {
        totalRevenue: 1890000,
        pendingPayments: 67000,
        lastPayment: '2024-09-12',
        rib: 'FR76 3000 4000 1100 0001 2345 89'
      },
      reservations: {
        total: 203,
        confirmed: 187,
        pending: 12,
        cancelled: 4
      },
      status: 'active',
      rating: 4.9,
      joinDate: '2018-11-08'
    },
    {
      id: 4,
      name: 'Ibiza Luxury Retreats',
      type: 'Silver Partner',
      location: 'Ibiza',
      contact: {
        manager: 'Carlos Rodriguez',
        email: 'carlos@ibizaluxury.es',
        phone: '+34 971 123 456'
      },
      portfolio: {
        totalVillas: 25,
        activeVillas: 22,
        destinations: ['Ibiza', 'Formentera'],
        averagePrice: 22000,
        occupancyRate: 68.7
      },
      commission: {
        standard: 10,
        premium: 13,
        exclusive: 15
      },
      financials: {
        totalRevenue: 567000,
        pendingPayments: 18000,
        lastPayment: '2024-09-05',
        rib: 'ES91 2100 0418 4502 0005 1332'
      },
      reservations: {
        total: 74,
        confirmed: 68,
        pending: 4,
        cancelled: 2
      },
      status: 'active',
      rating: 4.4,
      joinDate: '2021-04-12'
    }
  ];

  // Réservations détaillées par agence
  const reservationsByAgency = {
    1: [
      {
        id: 'RES-2024-157',
        villaName: 'Villa Azure Paradise',
        client: 'Jean-Pierre Moreau',
        dates: { checkin: '2024-10-15', checkout: '2024-10-22' },
        amount: 45000,
        deposit: 22500,
        paidAmount: 22500,
        commissionRate: 15,
        commissionAmount: 6750,
        status: 'confirmed',
        paymentStatus: 'deposit_received',
        agencyConfirmed: true,
        notes: 'Client VIP - Demande chef privé'
      },
      {
        id: 'RES-2024-158',
        villaName: 'Villa Sunset Dreams',
        client: 'Sarah Johnson',
        dates: { checkin: '2024-10-20', checkout: '2024-10-27' },
        amount: 38000,
        deposit: 19000,
        paidAmount: 0,
        commissionRate: 15,
        commissionAmount: 5700,
        status: 'pending',
        paymentStatus: 'pending',
        agencyConfirmed: false,
        notes: 'En attente confirmation agence'
      }
    ],
    2: [
      {
        id: 'RES-2024-159',
        villaName: 'Villa Cyclades View',
        client: 'Antonio Silva',
        dates: { checkin: '2024-10-12', checkout: '2024-10-19' },
        amount: 28000,
        deposit: 28000,
        paidAmount: 28000,
        commissionRate: 12,
        commissionAmount: 3360,
        status: 'confirmed',
        paymentStatus: 'fully_paid',
        agencyConfirmed: true,
        notes: 'Paiement intégral reçu'
      }
    ],
    3: [
      {
        id: 'RES-2024-160',
        villaName: 'Villa Riviera Prestige',
        client: 'Isabella Martinez',
        dates: { checkin: '2024-10-25', checkout: '2024-11-01' },
        amount: 52000,
        deposit: 26000,
        paidAmount: 52000,
        commissionRate: 16,
        commissionAmount: 8320,
        status: 'confirmed',
        paymentStatus: 'fully_paid',
        agencyConfirmed: true,
        notes: 'Séjour anniversaire VIP'
      }
    ]
  };

  // KPIs globaux
  const globalStats = {
    totalAgencies: agencies.length,
    activeAgencies: agencies.filter(a => a.status === 'active').length,
    totalVillas: agencies.reduce((sum, a) => sum + a.portfolio.totalVillas, 0),
    totalRevenue: agencies.reduce((sum, a) => sum + a.financials.totalRevenue, 0),
    averageCommission: 15.2,
    pendingReservations: agencies.reduce((sum, a) => sum + a.reservations.pending, 0)
  };

  const getPartnerTypeColor = (type) => {
    switch (type) {
      case 'Premium Partner': return 'bg-purple-100 text-purple-800';
      case 'Gold Partner': return 'bg-yellow-100 text-yellow-800';
      case 'Silver Partner': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'fully_paid': return 'text-green-600 bg-green-100';
      case 'deposit_received': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'fully_paid': return 'Intégralement payé';
      case 'deposit_received': return 'Deposit reçu';
      case 'pending': return 'En attente';
      default: return status;
    }
  };

  const filteredAgencies = agencies.filter(agency => {
    const matchesSearch = agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agency.contact.manager.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDestination = filterDestination === 'all' || 
                              agency.portfolio.destinations.includes(filterDestination);
    return matchesSearch && matchesDestination;
  });

  // Données des villas par agence (simulation)
  const villasByAgency = {
    1: [
      { id: 'V001', name: 'Villa Azure Paradise', destination: 'Saint-Barthélemy', bedrooms: 6, price: 45000, status: 'active', occupancy: 85 },
      { id: 'V002', name: 'Villa Sunset Dreams', destination: 'Saint-Barthélemy', bedrooms: 4, price: 38000, status: 'active', occupancy: 78 },
      { id: 'V003', name: 'Villa Crystal Bay', destination: 'Saint-Martin', bedrooms: 5, price: 42000, status: 'active', occupancy: 72 }
    ],
    2: [
      { id: 'V004', name: 'Villa Cyclades View', destination: 'Mykonos', bedrooms: 7, price: 35000, status: 'active', occupancy: 90 },
      { id: 'V005', name: 'Villa Aegean Breeze', destination: 'Santorini', bedrooms: 3, price: 28000, status: 'active', occupancy: 65 }
    ],
    3: [
      { id: 'V006', name: 'Villa Riviera Prestige', destination: 'Cannes', bedrooms: 8, price: 55000, status: 'active', occupancy: 95 },
      { id: 'V007', name: 'Villa Monaco Pearl', destination: 'Monaco', bedrooms: 6, price: 75000, status: 'active', occupancy: 88 }
    ]
  };

  const handleAgencyPortfolioClick = (agency) => {
    setSelectedAgency(agency);
    setShowAgencyPortfolio(true);
  };

  const handleAgencyFinancesClick = (agency) => {
    setSelectedAgency(agency);
    setShowAgencyFinances(true);
  };

  // Composant pour la vue détaillée du portefeuille d'une agence
  const AgencyPortfolioView = ({ agency, onBack }) => {
    const agencyVillas = villasByAgency[agency.id] || [];
    
    return (
      <div className="agency-portfolio-view">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Retour</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{agency.name}</h1>
                <p className="text-gray-600 mt-1">Portfolio de {agencyVillas.length} villas</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm rounded-full ${getPartnerTypeColor(agency.type)}`}>
                {agency.type}
              </span>
              <div className="flex items-center space-x-1">
                <Star className="text-yellow-500" size={16} />
                <span className="text-sm font-medium">{agency.rating}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Statistiques du portefeuille */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Building className="text-blue-600" size={20} />
                <span className="text-xs text-blue-600">Total</span>
              </div>
              <div className="text-xl font-bold text-gray-800">{agency.portfolio.totalVillas}</div>
              <div className="text-sm text-gray-600">Villas</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-xs text-green-600">Actives</span>
              </div>
              <div className="text-xl font-bold text-green-600">{agency.portfolio.activeVillas}</div>
              <div className="text-sm text-gray-600">En ligne</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="text-yellow-600" size={20} />
                <span className="text-xs text-yellow-600">Moyen</span>
              </div>
              <div className="text-xl font-bold text-yellow-600">€{agency.portfolio.averagePrice.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Prix</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="text-purple-600" size={20} />
                <span className="text-xs text-purple-600">Global</span>
              </div>
              <div className="text-xl font-bold text-purple-600">{agency.portfolio.occupancyRate}%</div>
              <div className="text-sm text-gray-600">Occupation</div>
            </div>
          </div>

          {/* Liste des villas */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Villas du Portefeuille</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <th className="p-4">Villa</th>
                    <th className="p-4">Destination</th>
                    <th className="p-4">Chambres</th>
                    <th className="p-4">Prix/Semaine</th>
                    <th className="p-4">Occupation</th>
                    <th className="p-4">Statut</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agencyVillas.map((villa) => (
                    <tr key={villa.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="font-medium text-gray-800">{villa.name}</div>
                        <div className="text-xs text-gray-500">ID: {villa.id}</div>
                      </td>
                      <td className="p-4 text-sm text-gray-900">{villa.destination}</td>
                      <td className="p-4 text-sm text-gray-900">{villa.bedrooms} chambres</td>
                      <td className="p-4 text-sm font-medium text-gray-900">€{villa.price.toLocaleString()}</td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-16 h-2 bg-gray-200 rounded">
                            <div 
                              className="h-full bg-blue-500 rounded" 
                              style={{ width: `${villa.occupancy}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600">{villa.occupancy}%</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          villa.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {villa.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-800">
                            <Eye size={14} />
                          </button>
                          <button className="text-gray-600 hover:text-gray-800">
                            <Edit size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Composant pour la vue détaillée des finances d'une agence
  const AgencyFinancesView = ({ agency, onBack }) => {
    const agencyReservations = reservationsByAgency[agency.id] || [];
    const totalCommissions = agencyReservations.reduce((sum, res) => sum + res.commissionAmount, 0);
    const paidCommissions = agencyReservations
      .filter(res => res.paymentStatus === 'fully_paid')
      .reduce((sum, res) => sum + res.commissionAmount, 0);
    const pendingCommissions = totalCommissions - paidCommissions;

    return (
      <div className="agency-finances-view">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Retour</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Finances - {agency.name}</h1>
                <p className="text-gray-600 mt-1">Détail financier et commissions</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <FileText size={18} />
                <span>Rapport Financier</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* KPIs Financiers */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="text-green-600" size={20} />
                <span className="text-xs text-green-600">Total</span>
              </div>
              <div className="text-xl font-bold text-green-600">€{(agency.financials.totalRevenue / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-600">Revenus</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="text-blue-600" size={20} />
                <span className="text-xs text-blue-600">Générées</span>
              </div>
              <div className="text-xl font-bold text-blue-600">€{(totalCommissions / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-600">Commissions</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="text-purple-600" size={20} />
                <span className="text-xs text-purple-600">Payées</span>
              </div>
              <div className="text-xl font-bold text-purple-600">€{(paidCommissions / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-600">Encaissées</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="text-orange-600" size={20} />
                <span className="text-xs text-orange-600">En attente</span>
              </div>
              <div className="text-xl font-bold text-orange-600">€{(pendingCommissions / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-600">À recevoir</div>
            </div>
          </div>

          {/* Détails des commissions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Taux de commission */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Taux de Commission</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Standard</span>
                  <span className="text-lg font-bold text-gray-800">{agency.commission.standard}%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-blue-700">Premium</span>
                  <span className="text-lg font-bold text-blue-800">{agency.commission.premium}%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-purple-700">Exclusive</span>
                  <span className="text-lg font-bold text-purple-800">{agency.commission.exclusive}%</span>
                </div>
              </div>
            </div>

            {/* Informations bancaires */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations Bancaires</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">RIB</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg font-mono text-sm">
                    {agency.financials.rib}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Dernier Paiement</label>
                  <div className="mt-1 text-sm font-medium text-gray-800">
                    {new Date(agency.financials.lastPayment).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Montant en Attente</label>
                  <div className="mt-1 text-lg font-bold text-red-600">
                    €{agency.financials.pendingPayments.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Historique des réservations et commissions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Historique des Commissions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                    <th className="p-4">Réservation</th>
                    <th className="p-4">Villa</th>
                    <th className="p-4">Client</th>
                    <th className="p-4">Montant Total</th>
                    <th className="p-4">Taux</th>
                    <th className="p-4">Commission</th>
                    <th className="p-4">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agencyReservations.map((reservation) => (
                    <tr key={reservation.id} className="hover:bg-gray-50">
                      <td className="p-4 text-sm font-medium text-blue-600">{reservation.id}</td>
                      <td className="p-4 text-sm text-gray-900">{reservation.villaName}</td>
                      <td className="p-4 text-sm text-gray-900">{reservation.client}</td>
                      <td className="p-4 text-sm font-medium text-gray-900">€{reservation.amount.toLocaleString()}</td>
                      <td className="p-4 text-sm text-gray-900">{reservation.commissionRate}%</td>
                      <td className="p-4 text-sm font-medium text-green-600">€{reservation.commissionAmount.toLocaleString()}</td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                          {getPaymentStatusLabel(reservation.paymentStatus)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Vérifier si on affiche une vue détaillée
  if (showAgencyPortfolio && selectedAgency) {
    return <AgencyPortfolioView agency={selectedAgency} onBack={() => setShowAgencyPortfolio(false)} />;
  }

  if (showAgencyFinances && selectedAgency) {
    return <AgencyFinancesView agency={selectedAgency} onBack={() => setShowAgencyFinances(false)} />;
  }

  return (
    <div className="agencies-management">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des Agences</h1>
            <p className="text-gray-600 mt-1">Gérez vos partenaires agences et leur portefeuille de villas</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <FileText size={18} />
              <span>Rapport</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Plus size={18} />
              <span>Nouvelle Agence</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Building className="text-blue-600" size={20} />
              <span className="text-xs text-blue-600">Total</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{globalStats.totalAgencies}</div>
            <div className="text-sm text-gray-600">Agences</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-green-600" size={20} />
              <span className="text-xs text-green-600">Actives</span>
            </div>
            <div className="text-xl font-bold text-green-600">{globalStats.activeAgencies}</div>
            <div className="text-sm text-gray-600">Partenaires</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-purple-600" size={20} />
              <span className="text-xs text-purple-600">Portfolio</span>
            </div>
            <div className="text-xl font-bold text-purple-600">{globalStats.totalVillas}</div>
            <div className="text-sm text-gray-600">Villas</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-yellow-600" size={20} />
              <span className="text-xs text-yellow-600">+12.8%</span>
            </div>
            <div className="text-xl font-bold text-yellow-600">€{(globalStats.totalRevenue / 1000000).toFixed(1)}M</div>
            <div className="text-sm text-gray-600">Revenus</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-orange-600" size={20} />
              <span className="text-xs text-orange-600">Moy.</span>
            </div>
            <div className="text-xl font-bold text-orange-600">{globalStats.averageCommission}%</div>
            <div className="text-sm text-gray-600">Commission</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-red-600" size={20} />
              <span className="text-xs text-red-600">Urgent</span>
            </div>
            <div className="text-xl font-bold text-red-600">{globalStats.pendingReservations}</div>
            <div className="text-sm text-gray-600">En Attente</div>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
                { id: 'reservations', label: 'Réservations', icon: Calendar },
                { id: 'portfolios', label: 'Portefeuilles', icon: PieChart },
                { id: 'financials', label: 'Finances', icon: CreditCard }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Onglet Vue d'ensemble */}
            {activeTab === 'overview' && (
              <div>
                {/* Filtres */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Rechercher une agence..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <select
                      value={filterDestination}
                      onChange={(e) => setFilterDestination(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Toutes destinations</option>
                      <option value="Saint-Barthélemy">Saint-Barthélemy</option>
                      <option value="Mykonos">Mykonos</option>
                      <option value="Cannes">Cannes</option>
                      <option value="Saint-Tropez">Saint-Tropez</option>
                      <option value="Ibiza">Ibiza</option>
                    </select>
                  </div>
                  <span className="text-sm text-gray-500">{filteredAgencies.length} agence(s)</span>
                </div>

                {/* Liste des agences */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredAgencies.map((agency) => (
                    <div key={agency.id} className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building className="text-blue-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-800">{agency.name}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`px-2 py-1 text-xs rounded-full ${getPartnerTypeColor(agency.type)}`}>
                                {agency.type}
                              </span>
                              <div className="flex items-center space-x-1">
                                <Star className="text-yellow-500" size={12} />
                                <span className="text-xs text-gray-600">{agency.rating}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedAgency(agency)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={18} />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-xl font-bold text-gray-800">{agency.portfolio.totalVillas}</div>
                          <div className="text-xs text-gray-600">Villas</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-600">€{(agency.financials.totalRevenue / 1000).toFixed(0)}K</div>
                          <div className="text-xs text-gray-600">Revenus</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">{agency.portfolio.occupancyRate}%</div>
                          <div className="text-xs text-gray-600">Occupation</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{agency.location}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users size={14} />
                          <span>{agency.contact.manager}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-xs text-gray-600">
                            <span className="font-medium text-green-600">{agency.reservations.confirmed}</span> confirmées
                          </div>
                          <div className="text-xs text-gray-600">
                            <span className="font-medium text-yellow-600">{agency.reservations.pending}</span> en attente
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="text-gray-400 hover:text-blue-600">
                            <Phone size={14} />
                          </button>
                          <button className="text-gray-400 hover:text-green-600">
                            <Mail size={14} />
                          </button>
                          <button className="text-gray-400 hover:text-purple-600">
                            <MessageSquare size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Réservations */}
            {activeTab === 'reservations' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Suivi des Réservations par Agence</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Toutes agences</span>
                    <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option>Toutes les agences</option>
                      {agencies.map(agency => (
                        <option key={agency.id}>{agency.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        <th className="pb-3">Réservation</th>
                        <th className="pb-3">Villa</th>
                        <th className="pb-3">Client</th>
                        <th className="pb-3">Agence</th>
                        <th className="pb-3">Dates</th>
                        <th className="pb-3">Montant Total</th>
                        <th className="pb-3">Montant Payé</th>
                        <th className="pb-3">Commission</th>
                        <th className="pb-3">Statut</th>
                        <th className="pb-3">Paiement</th>
                        <th className="pb-3">Confirmation</th>
                        <th className="pb-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {Object.entries(reservationsByAgency).map(([agencyId, reservations]) =>
                        reservations.map((reservation) => {
                          const agency = agencies.find(a => a.id === parseInt(agencyId));
                          return (
                            <tr key={reservation.id} className="hover:bg-gray-50">
                              <td className="py-4 text-sm font-medium text-blue-600">
                                {reservation.id}
                              </td>
                              <td className="py-4 text-sm text-gray-900">
                                {reservation.villaName}
                              </td>
                              <td className="py-4 text-sm text-gray-900">
                                {reservation.client}
                              </td>
                              <td className="py-4 text-sm text-gray-900">
                                {agency?.name}
                              </td>
                              <td className="py-4 text-sm text-gray-900">
                                {new Date(reservation.dates.checkin).toLocaleDateString('fr-FR')} - 
                                {new Date(reservation.dates.checkout).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="py-4 text-sm font-medium text-gray-900">
                                €{reservation.amount.toLocaleString()}
                              </td>
                              <td className="py-4 text-sm font-medium text-green-600">
                                €{reservation.paidAmount.toLocaleString()}
                              </td>
                              <td className="py-4 text-sm">
                                <div className="text-sm font-medium text-blue-600">
                                  €{reservation.commissionAmount.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {reservation.commissionRate}%
                                </div>
                              </td>
                              <td className="py-4">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                                  {reservation.status === 'confirmed' ? 'Confirmé' : 
                                   reservation.status === 'pending' ? 'En attente' : 'Annulé'}
                                </span>
                              </td>
                              <td className="py-4">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                                  {getPaymentStatusLabel(reservation.paymentStatus)}
                                </span>
                              </td>
                              <td className="py-4">
                                {reservation.agencyConfirmed ? (
                                  <CheckCircle className="text-green-600" size={16} />
                                ) : (
                                  <Clock className="text-yellow-600" size={16} />
                                )}
                              </td>
                              <td className="py-4">
                                <div className="flex items-center space-x-2">
                                  <button className="text-blue-600 hover:text-blue-800">
                                    <Eye size={14} />
                                  </button>
                                  <button className="text-gray-600 hover:text-gray-800">
                                    <Edit size={14} />
                                  </button>
                                  <button className="text-purple-600 hover:text-purple-800">
                                    <MessageSquare size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Onglet Portefeuilles */}
            {activeTab === 'portfolios' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {agencies.map((agency) => (
                    <div key={agency.id} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 
                          className="font-medium text-gray-800 hover:text-blue-600 cursor-pointer transition-colors"
                          onClick={() => handleAgencyPortfolioClick(agency)}
                        >
                          {agency.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getPartnerTypeColor(agency.type)}`}>
                          {agency.type}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">{agency.portfolio.totalVillas}</div>
                          <div className="text-xs text-gray-600">Total Villas</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{agency.portfolio.activeVillas}</div>
                          <div className="text-xs text-gray-600">Actives</div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Répartition par Destination</div>
                        <div className="space-y-2">
                          {agency.portfolio.destinations.map((dest, index) => (
                            <div key={dest} className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">{dest}</span>
                              <div className="flex items-center space-x-2">
                                <div className="w-16 h-2 bg-gray-200 rounded">
                                  <div 
                                    className="h-full bg-blue-500 rounded" 
                                    style={{ width: `${(index + 1) * 25}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {Math.floor(agency.portfolio.totalVillas / agency.portfolio.destinations.length * (index + 1) / agency.portfolio.destinations.length)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-xs text-gray-600">
                        <div>
                          <div className="font-medium">Prix Moyen</div>
                          <div>€{agency.portfolio.averagePrice.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="font-medium">Occupation</div>
                          <div>{agency.portfolio.occupancyRate}%</div>
                        </div>
                        <div>
                          <div className="font-medium">Commission</div>
                          <div>{agency.commission.standard}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Finances */}
            {activeTab === 'financials' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {agencies.map((agency) => (
                    <div 
                      key={agency.id} 
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => handleAgencyFinancesClick(agency)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-gray-800">{agency.name}</h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">RIB</span>
                          <button className="text-blue-600 hover:text-blue-800">
                            <Eye size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-lg font-bold text-green-600">
                            €{(agency.financials.totalRevenue / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs text-gray-600">Revenus Total</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-lg font-bold text-yellow-600">
                            €{(agency.financials.pendingPayments / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs text-gray-600">En Attente</div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Commission Standard</span>
                          <span className="font-medium">{agency.commission.standard}%</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Commission Premium</span>
                          <span className="font-medium">{agency.commission.premium}%</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Commission Exclusive</span>
                          <span className="font-medium">{agency.commission.exclusive}%</span>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Dernier Paiement</span>
                          <span className="font-medium">
                            {new Date(agency.financials.lastPayment).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenciesManagement;